import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';
import { FaCalendarAlt, FaDollarSign, FaHome } from 'react-icons/fa';
import style from './PendingPayments.module.css';

const PendingPayments = () => {
  const navigate = useNavigate();
  const [pendingPayments, setPendingPayments] = useState([]);
  const [apiSummary, setApiSummary] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  const toNumber = (value) => {
    const parsed = Number(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const getDaysUntilEndOfMonth = () => {
    const today = new Date();
    const endOfMonth = new Date(today.getFullYear(), today.getMonth() + 1, 0);
    const diffMs = endOfMonth - today;
    const days = Math.ceil(diffMs / (1000 * 60 * 60 * 24));
    return Math.max(days, 1);
  };

  useEffect(() => {
    const loadPendingPayments = async () => {
      try {
        setLoading(true);
        setError('');
        const days = getDaysUntilEndOfMonth();
        const response = await axios.get(`/rentals/admin/payments/upcoming?days=${days}`);
        const payload = response.data?.data || {};
        const payments = Array.isArray(payload.upcomingPayments)
          ? payload.upcomingPayments
          : [];

        const onlyPending = payments.filter((payment) => payment.paymentStatus === 'pending');

        setPendingPayments(onlyPending);
        setApiSummary(payload.summary || null);
      } catch (requestError) {
        setError(
          requestError.response?.data?.message ||
            requestError.response?.data?.error ||
            requestError.message
        );
      } finally {
        setLoading(false);
      }
    };

    loadPendingPayments();
  }, []);

  const totalPendingAmount = useMemo(
    () =>
      pendingPayments.reduce(
        (acc, payment) => acc + toNumber(payment.totalAmount),
        0
      ),
    [pendingPayments]
  );

  const getAlertLevel = (payment) => {
    if (payment?.alertLevel) return payment.alertLevel;
    const daysUntilDue = toNumber(payment?.daysUntilDue);
    if (daysUntilDue <= 3) return 'danger';
    if (daysUntilDue <= 7) return 'warning';
    return 'info';
  };

  const getAlertLabel = (level) => {
    if (level === 'danger') return 'Vence ya';
    if (level === 'warning') return 'Próximo';
    return 'A tiempo';
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('es-AR', {
      style: 'currency',
      currency: 'ARS',
      minimumFractionDigits: 0
    }).format(amount || 0);
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('es-AR');
  };

  if (loading) {
    return <div className={style.loading}>Cargando pagos pendientes del mes...</div>;
  }

  if (error) {
    return <div className={style.error}>Error: {error}</div>;
  }

  return (
    <div className={style.container}>
      <div className={style.header}>
        <h1 className={style.title}>
          <FaCalendarAlt className={style.titleIcon} />
          Pagos Pendientes del Mes
        </h1>

        <div className={style.summary}>
          <div className={style.summaryItem}>
            <span className={style.summaryLabel}>Cuotas pendientes:</span>
            <span className={style.summaryValue}>
              {apiSummary?.totalPayments ?? pendingPayments.length}
            </span>
          </div>
          <div className={style.summaryItem}>
            <span className={style.summaryLabel}>Monto pendiente:</span>
            <span className={`${style.summaryValue} ${style.amount}`}>
              {formatCurrency(apiSummary?.totalAmount ?? totalPendingAmount)}
            </span>
          </div>
          <div className={style.summaryItem}>
            <span className={style.summaryLabel}>Alertas críticas:</span>
            <span className={`${style.summaryValue} ${style.danger}`}>
              {apiSummary?.dangerAlerts ?? pendingPayments.filter((p) => getAlertLevel(p) === 'danger').length}
            </span>
          </div>
          <div className={style.summaryItem}>
            <span className={style.summaryLabel}>Alertas próximas:</span>
            <span className={`${style.summaryValue} ${style.warning}`}>
              {apiSummary?.warningAlerts ?? pendingPayments.filter((p) => getAlertLevel(p) === 'warning').length}
            </span>
          </div>
        </div>
      </div>

      {pendingPayments.length === 0 ? (
        <div className={style.noData}>
          <FaCalendarAlt className={style.noDataIcon} />
          <h3>No hay pagos pendientes para este mes</h3>
          <p>Todo está al día por ahora.</p>
        </div>
      ) : (
        <div className={style.list}>
          {pendingPayments.map((payment) => {
            const alertLevel = getAlertLevel(payment);

            return (
            <div key={payment.id} className={`${style.card} ${style[alertLevel]}`}>
              <div className={style.cardTop}>
                <div className={style.property}>
                  <FaHome />
                  <div>
                    <h3>{payment.rental?.property?.title || 'Propiedad sin título'}</h3>
                    <p>
                      {payment.rental?.property?.street} {payment.rental?.property?.number},{' '}
                      {payment.rental?.property?.locality}
                    </p>
                  </div>
                </div>

                <div className={style.amountBlock}>
                  <span className={`${style.alertBadge} ${style[alertLevel]}`}>
                    {getAlertLabel(alertLevel)}
                  </span>
                  <span>Monto</span>
                  <strong>{formatCurrency(payment.totalAmount)}</strong>
                </div>
              </div>

              <div className={style.cardBottom}>
                <span>
                  Vence: {formatDate(payment.dueDate)}
                  {' • '}
                  {toNumber(payment.daysUntilDue)} días
                </span>
                <button
                  type="button"
                  className={style.detailBtn}
                  onClick={() => navigate(`/alquileres/contrato/${payment.rentalId}/pagos`)}
                >
                  Ver contrato/pagos
                </button>
              </div>
            </div>
          );})}
        </div>
      )}

      <div className={style.footerAction}>
        <button type="button" className={style.backBtn} onClick={() => navigate('/alquileres')}>
          Volver al dashboard
        </button>
        <button type="button" className={style.primaryBtn} onClick={() => navigate('/alquileres/vencidos')}>
          Ir a pagos vencidos
        </button>
      </div>
    </div>
  );
};

export default PendingPayments;
